import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell } from '@/components/ui-bits';
import { ListTree, Plus, Save, Trash2, X, Users, Settings2, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  name: '',
  description: '',
  color: '#0d9488',
  kind: 'custom',
  mode: 'hybrid',
  shared: true,
  active: true,
  order: 100,
  default_assignee: '',
  filters: {
    status: '',
    stage_id: '',
    source: '',
    tag: '',
    assigned_to: '',
    has_phone: null,
    has_email: null,
    custom: [],
  },
  columns: {
    system: ['first_name', 'last_name', 'phone_e164', 'email', 'status'],
    custom: [],
  },
};

export default function LeadListsPage() {
  const nav = useNavigate();
  const [lists, setLists] = useState(null);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const load = async () => {
    try {
      const [L, M] = await Promise.all([
        api.get('/lead-lists'),
        api.get('/lead-lists/meta/fields'),
      ]);
      setLists(L.data.lists || []);
      setMeta(M.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar listas');
      setLists([]);
    }
  };

  useEffect(() => {
    load();
    api.get('/pipeline').then((r) => setStages((r.data.pipeline?.stages || []).sort((a, b) => a.order - b.order))).catch(() => {});
    api.get('/admin/users').then((r) => setUsers(r.data.users || [])).catch(() => {});
    api.get('/tags').then((r) => setTags(r.data.tags || [])).catch(() => {});
  }, []);

  const openNew = () => setEditing({ ...EMPTY, filters: { ...EMPTY.filters, custom: [] }, columns: { ...EMPTY.columns } });

  const openEdit = (lst) => {
    setEditing({
      ...lst,
      default_assignee: lst.default_assignee || '',
      filters: {
        status: '',
        stage_id: '',
        source: '',
        tag: '',
        assigned_to: '',
        has_phone: null,
        has_email: null,
        custom: [],
        ...(lst.filters || {}),
        custom: (lst.filters?.custom || []).map((c) => ({ ...c })),
      },
      columns: {
        system: [...(lst.columns?.system || EMPTY.columns.system)],
        custom: [...(lst.columns?.custom || [])],
      },
    });
  };

  const importToList = async (lst, file) => {
    if (!file) return;
    setImportingId(lst.id);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(
        `/leads/import?list_id=${encodeURIComponent(lst.id)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Importados ${data.created} a “${lst.name}”`, {
        description: `${data.duplicates} duplicados · ${data.added_to_list ?? 0} en lista · ${data.invalid} inválidos`,
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al importar');
    } finally {
      setImportingId(null);
    }
  };

  const save = async () => {
    if (!editing.name?.trim()) return toast.error('Nombre requerido');
    setSaving(true);
    const payload = {
      name: editing.name.trim(),
      description: editing.description || null,
      color: editing.color,
      kind: editing.kind,
      mode: editing.mode,
      shared: editing.shared !== false,
      active: editing.active !== false,
      order: Number(editing.order) || 100,
      default_assignee: editing.default_assignee || null,
      filters: {
        status: editing.filters.status || null,
        stage_id: editing.filters.stage_id || null,
        source: editing.filters.source || null,
        tag: editing.filters.tag || null,
        assigned_to: editing.filters.assigned_to || null,
        has_phone: editing.filters.has_phone,
        has_email: editing.filters.has_email,
        custom: (editing.filters.custom || []).filter((c) => c.key),
      },
      columns: {
        system: editing.columns.system || [],
        custom: editing.columns.custom || [],
      },
    };
    try {
      if (editing.id) {
        await api.patch(`/lead-lists/${editing.id}`, payload);
        toast.success('Lista actualizada');
      } else {
        await api.post('/lead-lists', payload);
        toast.success('Lista creada');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const del = async (lst) => {
    if (!confirm(`¿Eliminar lista "${lst.name}"? Los leads no se borran, solo la lista.`)) return;
    try {
      await api.delete(`/lead-lists/${lst.id}`);
      toast.success('Lista eliminada');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const seedExamples = async () => {
    try {
      const { data } = await api.post('/lead-lists/seed-examples?force=true');
      toast.success(
        data.created
          ? `${data.created} listas de ejemplo · ${data.fields_created || 0} campos nuevos`
          : (data.message || 'OK'),
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear ejemplos');
    }
  };

  const toggleCol = (kind, key) => {
    setEditing((prev) => {
      const arr = [...(prev.columns[kind] || [])];
      const i = arr.indexOf(key);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(key);
      return { ...prev, columns: { ...prev.columns, [kind]: arr } };
    });
  };

  const addCustomFilter = () => {
    setEditing((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        custom: [...(prev.filters.custom || []), { key: '', op: 'eq', value: '' }],
      },
    }));
  };

  if (!lists) return <LoadingShell />;

  const kinds = meta?.kinds || {};
  const modes = meta?.modes || {};
  const sysFields = meta?.system_fields || [];
  const customFields = meta?.custom_fields || [];

  return (
    <div>
      <PageHeader
        title="Listas de leads"
        description="Listas independientes (carteras, recuperación, vencidos…). Cada una con filtros y columnas propias."
        actions={(
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={seedExamples}>
              <Sparkles className="h-4 w-4" /> Ejemplos
            </button>
            <button type="button" className="btn-primary" onClick={openNew}>
              <Plus className="h-4 w-4" /> Nueva lista
            </button>
          </div>
        )}
      />

      {lists.length === 0 ? (
        <EmptyState
          icon={ListTree}
          title="Sin listas aún"
          description="Crea listas independientes: activos, recuperación, vencidos, etc. Configura columnas con campos del sistema y personalizados."
          cta={(
            <div className="flex gap-2">
              <button type="button" className="btn-primary" onClick={openNew}>Crear lista</button>
            </div>
          )}
        />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {lists.map((lst) => (
            <div key={lst.id} className="surface p-4 border-l-4" style={{ borderLeftColor: lst.color || '#0d9488' }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{lst.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {lst.kind_label || lst.kind} · {lst.mode}
                    {lst.active === false ? ' · inactiva' : ''}
                  </div>
                </div>
                <span className="chip tabular-nums shrink-0">
                  <Users className="h-3 w-3" /> {lst.lead_count ?? '—'}
                </span>
              </div>
              {lst.description && (
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{lst.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1">
                <button type="button" className="btn-primary h-8 text-xs" onClick={() => nav(`/app/leads?list=${lst.id}`)}>
                  Ver leads
                </button>
                <button type="button" className="btn-ghost h-8 text-xs" onClick={() => openEdit(lst)}>
                  <Settings2 className="h-3.5 w-3.5" /> Configurar
                </button>
                <label
                  className="btn-ghost h-8 text-xs cursor-pointer inline-flex items-center gap-1"
                  title="Importar CSV/XLSX a esta lista"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {importingId === lst.id ? 'Importando…' : 'Importar'}
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    disabled={importingId === lst.id}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      importToList(lst, f);
                    }}
                  />
                </label>
                <button type="button" className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]" onClick={() => del(lst)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="surface w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">
                {editing.id ? 'Editar lista' : 'Nueva lista de leads'}
              </h2>
              <button type="button" className="btn-ghost h-8 w-8 p-0" onClick={() => setEditing(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <label className="block text-xs sm:col-span-2">
                Nombre
                <input className="input mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej. Préstamos en efectivo activos" />
              </label>
              <label className="block text-xs sm:col-span-2">
                Descripción
                <input className="input mt-1" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Surtidora León, recuperación, vencidos…" />
              </label>
              <label className="block text-xs">
                Tipo / objetivo
                <select className="input mt-1" value={editing.kind || 'custom'} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {Object.entries(kinds).map(([k, lab]) => (
                    <option key={k} value={k}>{lab}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Modo de membresía
                <select className="input mt-1" value={editing.mode || 'hybrid'} onChange={(e) => setEditing({ ...editing, mode: e.target.value })}>
                  {Object.entries(modes).map(([k, lab]) => (
                    <option key={k} value={k}>{lab}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Color
                <div className="flex gap-2 mt-1">
                  <input type="color" className="h-10 w-12 rounded" value={editing.color || '#0d9488'} onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
                  <input className="input" value={editing.color || ''} onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
                </div>
              </label>
              <label className="block text-xs">
                Orden
                <input type="number" className="input mt-1" value={editing.order ?? 100} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
              </label>
              <label className="block text-xs sm:col-span-2">
                Asignado por defecto (al agregar o importar a esta lista)
                <select
                  className="input mt-1"
                  value={editing.default_assignee || ''}
                  onChange={(e) => setEditing({ ...editing, default_assignee: e.target.value })}
                >
                  <option value="">— sin cambio (auto-asignación del tenant) —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs sm:col-span-2 mt-1">
                <input
                  type="checkbox"
                  checked={editing.active !== false}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                Lista activa (visible en Leads y Campañas)
              </label>
              <label className="flex items-center gap-2 text-xs sm:col-span-2">
                <input
                  type="checkbox"
                  checked={editing.shared !== false}
                  onChange={(e) => setEditing({ ...editing, shared: e.target.checked })}
                />
                Compartida con el equipo
              </label>
            </div>

            {/* Dynamic filters */}
            {(editing.mode === 'dynamic' || editing.mode === 'hybrid') && (
              <div className="mt-5 border-t border-border pt-4">
                <h3 className="font-medium text-sm mb-2">Filtros dinámicos</h3>
                <div className="grid sm:grid-cols-2 gap-2 text-xs">
                  <label>Estado
                    <select className="input mt-1" value={editing.filters.status || ''} onChange={(e) => setEditing({ ...editing, filters: { ...editing.filters, status: e.target.value } })}>
                      <option value="">— cualquiera —</option>
                      <option value="open">Abierto</option>
                      <option value="won">Ganado</option>
                      <option value="lost">Perdido</option>
                      <option value="closed">Cerrado</option>
                    </select>
                  </label>
                  <label>Etapa
                    <select className="input mt-1" value={editing.filters.stage_id || ''} onChange={(e) => setEditing({ ...editing, filters: { ...editing.filters, stage_id: e.target.value } })}>
                      <option value="">— cualquiera —</option>
                      {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </label>
                  <label>Origen
                    <input className="input mt-1" value={editing.filters.source || ''} onChange={(e) => setEditing({ ...editing, filters: { ...editing.filters, source: e.target.value } })} />
                  </label>
                  <label>Tag
                    <select className="input mt-1" value={editing.filters.tag || ''} onChange={(e) => setEditing({ ...editing, filters: { ...editing.filters, tag: e.target.value } })}>
                      <option value="">— cualquiera —</option>
                      {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
                    </select>
                  </label>
                  <label>Asignado
                    <select className="input mt-1" value={editing.filters.assigned_to || ''} onChange={(e) => setEditing({ ...editing, filters: { ...editing.filters, assigned_to: e.target.value } })}>
                      <option value="">— cualquiera —</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  </label>
                  <label>Teléfono
                    <select
                      className="input mt-1"
                      value={editing.filters.has_phone === true ? '1' : editing.filters.has_phone === false ? '0' : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditing({
                          ...editing,
                          filters: {
                            ...editing.filters,
                            has_phone: v === '1' ? true : v === '0' ? false : null,
                          },
                        });
                      }}
                    >
                      <option value="">— cualquiera —</option>
                      <option value="1">Con teléfono</option>
                      <option value="0">Sin teléfono</option>
                    </select>
                  </label>
                  <label>Email
                    <select
                      className="input mt-1"
                      value={editing.filters.has_email === true ? '1' : editing.filters.has_email === false ? '0' : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditing({
                          ...editing,
                          filters: {
                            ...editing.filters,
                            has_email: v === '1' ? true : v === '0' ? false : null,
                          },
                        });
                      }}
                    >
                      <option value="">— cualquiera —</option>
                      <option value="1">Con email</option>
                      <option value="0">Sin email</option>
                    </select>
                  </label>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-muted-foreground">Filtros por campo personalizado</span>
                    <button type="button" className="btn-ghost h-7 text-xs" onClick={addCustomFilter}><Plus className="h-3 w-3" /> Agregar</button>
                  </div>
                  {(editing.filters.custom || []).map((cf, i) => (
                    <div key={i} className="grid grid-cols-[1fr_100px_1fr_32px] gap-1 mb-1">
                      <select
                        className="input text-xs"
                        value={cf.key || ''}
                        onChange={(e) => {
                          const custom = [...editing.filters.custom];
                          custom[i] = { ...custom[i], key: e.target.value };
                          setEditing({ ...editing, filters: { ...editing.filters, custom } });
                        }}
                      >
                        <option value="">Campo…</option>
                        {customFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      <select
                        className="input text-xs"
                        value={cf.op || 'eq'}
                        onChange={(e) => {
                          const custom = [...editing.filters.custom];
                          custom[i] = { ...custom[i], op: e.target.value };
                          setEditing({ ...editing, filters: { ...editing.filters, custom } });
                        }}
                      >
                        <option value="eq">=</option>
                        <option value="neq">≠</option>
                        <option value="gt">&gt;</option>
                        <option value="gte">≥</option>
                        <option value="lt">&lt;</option>
                        <option value="lte">≤</option>
                        <option value="contains">contiene</option>
                        <option value="exists">existe</option>
                      </select>
                      <input
                        className="input text-xs"
                        value={cf.value === true ? 'true' : cf.value === false ? 'false' : (cf.value ?? '')}
                        onChange={(e) => {
                          let v = e.target.value;
                          if (v === 'true') v = true;
                          else if (v === 'false') v = false;
                          else if (v !== '' && !Number.isNaN(Number(v)) && String(Number(v)) === v) v = Number(v);
                          const custom = [...editing.filters.custom];
                          custom[i] = { ...custom[i], value: v };
                          setEditing({ ...editing, filters: { ...editing.filters, custom } });
                        }}
                        placeholder="valor (true/false/número/texto)"
                      />
                      <button
                        type="button"
                        className="btn-ghost h-9 w-8 p-0 text-[hsl(var(--danger))]"
                        onClick={() => {
                          const custom = editing.filters.custom.filter((_, ix) => ix !== i);
                          setEditing({ ...editing, filters: { ...editing.filters, custom } });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Ej. préstamo_activo = true · dias_vencido ≥ 1 · status perdido para recuperación.
                  </p>
                </div>
              </div>
            )}

            {/* Columns */}
            <div className="mt-5 border-t border-border pt-4">
              <h3 className="font-medium text-sm mb-2">Columnas visibles en esta lista</h3>
              <p className="text-[11px] text-muted-foreground mb-2">
                Elige campos del sistema y personalizados (independiente de otras listas).
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Sistema</div>
                  <div className="max-h-40 overflow-y-auto border border-border rounded-[10px] p-2 space-y-1">
                    {sysFields.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={(editing.columns.system || []).includes(f.key)}
                          onChange={() => toggleCol('system', f.key)}
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Personalizados</div>
                  <div className="max-h-40 overflow-y-auto border border-border rounded-[10px] p-2 space-y-1">
                    {customFields.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Sin campos. Créalos en <Link to="/app/custom-fields" className="text-primary underline">Campos personalizados</Link>.
                      </p>
                    )}
                    {customFields.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={(editing.columns.custom || []).includes(f.key)}
                          onChange={() => toggleCol('custom', f.key)}
                        />
                        {f.label} <span className="font-mono text-[10px] text-muted-foreground">{f.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="btn-primary" disabled={saving} onClick={save}>
                <Save className="h-4 w-4" /> {saving ? 'Guardando…' : 'Guardar lista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
